import { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Select,
  Switch,
  Button,
  Group,
  Stack,
} from "@mantine/core";
import type {
  PayoutDestinationCategory,
  SelcomReferenceType,
  SelcomCatalogueEntry,
} from "../../types/domain";

const CATEGORY_OPTIONS: { value: PayoutDestinationCategory; label: string }[] = [
  { value: "bank", label: "Bank" },
  { value: "mobile-money", label: "Mobile Money" },
  { value: "selcom-pesa", label: "Selcom Pesa" },
];

const REFERENCE_TYPE_OPTIONS: { value: SelcomReferenceType; label: string }[] = [
  { value: "NUMERIC", label: "Numeric (digits only)" },
  { value: "ALPHANUMERIC", label: "Alphanumeric" },
];

export interface PayoutCatalogueFormValues {
  name: string;
  shortcode: string;
  category: PayoutDestinationCategory;
  referenceType: SelcomReferenceType;
  lookupEnabled: boolean;
  isActive: boolean;
}

export function PayoutCatalogueFormModal({
  opened,
  onClose,
  editing,
  loading,
  onSubmit,
}: {
  opened: boolean;
  onClose: () => void;
  editing: SelcomCatalogueEntry | null;
  loading: boolean;
  onSubmit: (values: PayoutCatalogueFormValues) => void;
}) {
  const [v, setV] = useState<PayoutCatalogueFormValues>({
    name: "",
    shortcode: "",
    category: "bank",
    referenceType: "NUMERIC",
    lookupEnabled: true,
    isActive: true,
  });

  useEffect(() => {
    if (opened) {
      setV({
        name: editing?.name ?? "",
        shortcode: editing?.shortcode ?? "",
        category: editing?.category ?? "bank",
        referenceType: editing?.referenceType ?? "NUMERIC",
        lookupEnabled: editing?.lookupEnabled ?? true,
        isActive: editing?.isActive ?? true,
      });
    }
  }, [opened, editing]);

  const isEdit = !!editing;
  const valid =
    v.name.trim() && v.shortcode.trim() && v.category && v.referenceType;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? "Edit payout destination" : "New payout destination"}
      centered
    >
      <Stack>
        <TextInput
          label="Name"
          placeholder="e.g. CRDB Bank"
          value={v.name}
          onChange={(e) => setV({ ...v, name: e.currentTarget.value })}
          required
        />
        <TextInput
          label="Shortcode"
          description={
            isEdit
              ? "Immutable — saved driver destinations reference it directly."
              : "Selcom institution code, e.g. CRDB, MPESA, SELCOM."
          }
          placeholder="e.g. CRDB"
          value={v.shortcode}
          onChange={(e) =>
            setV({ ...v, shortcode: e.currentTarget.value.toUpperCase() })
          }
          disabled={isEdit}
          required
        />
        <Select
          label="Category"
          data={CATEGORY_OPTIONS}
          value={v.category}
          onChange={(val) =>
            setV({ ...v, category: (val as PayoutDestinationCategory) ?? "bank" })
          }
          allowDeselect={false}
          disabled={isEdit}
          description={isEdit ? "Not editable once created." : undefined}
          required
        />
        <Select
          label="Reference number format"
          data={REFERENCE_TYPE_OPTIONS}
          value={v.referenceType}
          onChange={(val) =>
            setV({
              ...v,
              referenceType: (val as SelcomReferenceType) ?? "NUMERIC",
            })
          }
          allowDeselect={false}
          required
        />
        <Switch
          label="Verify account with Selcom before saving/paying out"
          description="When off, drivers can save/cash out to this destination unverified."
          checked={v.lookupEnabled}
          onChange={(e) =>
            setV({ ...v, lookupEnabled: e.currentTarget.checked })
          }
        />
        <Switch
          label="Active"
          description="Inactive destinations are hidden from the driver app but existing saved destinations keep working."
          checked={v.isActive}
          onChange={(e) => setV({ ...v, isActive: e.currentTarget.checked })}
        />
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!valid}
            onClick={() => onSubmit(v)}
          >
            {isEdit ? "Save changes" : "Create destination"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
